import { useState, useEffect } from "react";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Select from "../../../designSystem/b-molecules/formElements/select/Select";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import Input from "../../../designSystem/b-molecules/formElements/input/Input";
import Icon from "../../../designSystem/a-atoms/icons/Icon";
import { ref, update, onValue, get } from "firebase/database";
import { db } from "../../../configs/Firebase";
import PersonaLinkSelector from "../../personas/components/PersonaLinkSelector";
import style from "./EditThemeDialog.module.css";

const EditThemeDialog = ({ 
  show, 
  onClose, 
  record, 
  themes, 
  orgId, 
  uploadId,
  onThemeUpdated 
}) => {
  const [selectedTheme, setSelectedTheme] = useState(record?.themeName || "");
  const [selectedSubTheme, setSelectedSubTheme] = useState(record?.subThemeName || "");
  const [selectedSentiment, setSelectedSentiment] = useState(record?.sentiment || "neutral");
  const [selectedPersonaId, setSelectedPersonaId] = useState(record?.personaId || null);
  const [subThemes, setSubThemes] = useState([]);
  const [newThemeName, setNewThemeName] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (show && record) {
      setSelectedTheme(record.themeName || "");
      setSelectedSubTheme(record.subThemeName || "");
      setSelectedSentiment(record.sentiment || "neutral");
      setSelectedPersonaId(record.personaId || null);
      setNewThemeName("");
      setCreatingNew(false);
      setError("");
    }
  }, [show, record]);

  // Load sub-themes for the selected theme
  useEffect(() => {
    if (orgId && selectedTheme) {
      const theme = themes.find(t => t.name === selectedTheme);
      if (theme) {
        const subThemesRef = ref(db, `userData/${orgId}/customers/themes/${theme.id}/subThemes`);
        onValue(subThemesRef, (snapshot) => {
          const data = snapshot.val();
          const subThemesList = [];
          if (data) {
            for (let i in data) {
              subThemesList.push({
                id: i,
                ...data[i],
              });
            }
          }
          setSubThemes(subThemesList);
        });
      } else {
        setSubThemes([]);
      }
    } else {
      setSubThemes([]);
    }
  }, [orgId, selectedTheme, themes]);

  const themeOptions = themes.map(theme => ({ value: theme.name }));
  
  const sentimentOptions = [
    { value: "Positive", sentimentValue: "positive" },
    { value: "Negative", sentimentValue: "negative" },
    { value: "Neutral", sentimentValue: "neutral" }
  ];
  
  const getSentimentDisplayValue = (sentiment) => {
    const option = sentimentOptions.find(opt => opt.sentimentValue === sentiment);
    return option ? option.value : "Neutral";
  };

  function handleThemeChange(e, index) {
    if (index === themeOptions.length) {
      // "Create new" option
      setCreatingNew(true);
      setSelectedTheme("");
      setSelectedSubTheme(""); // Clear sub-theme when theme changes
    } else {
      setSelectedTheme(themeOptions[index].value);
      setCreatingNew(false);
      setSelectedSubTheme(""); // Clear sub-theme when theme changes
    }
  }

  async function handleSave() {
    if (updating) {
      return; // Prevent multiple simultaneous updates
    }

    if (creatingNew) {
      if (!newThemeName || newThemeName.trim() === "") {
        setError("Please enter a theme name");
        return;
      }
      // Create new theme and assign
      await updateTheme(newThemeName);
    } else {
      if (!selectedTheme) {
        setError("Please select a theme");
        return;
      }
      // Assign existing theme
      await updateTheme(selectedTheme);
    }
  }

  async function updateTheme(themeName) {
    if (!orgId) {
      setError("Organization ID is missing");
      return;
    }

    if (!uploadId) {
      setError("Upload ID is missing");
      return;
    }

    if (!record || !record.id) {
      setError("Record ID is missing");
      return;
    }

    setUpdating(true);
    setError("");

    try {
      const themeId = themeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      
      // Find sub-theme ID if one is selected
      let subThemeId = null;
      let subThemeName = null;
      if (selectedSubTheme) {
        const subTheme = subThemes.find(st => st.name === selectedSubTheme);
        if (subTheme) {
          subThemeId = subTheme.id;
          subThemeName = subTheme.name;
        }
      }

      // Update record's theme, sub-theme, sentiment, and persona
      const recordPath = `userData/${orgId}/customers/data/${uploadId}/records/${record.id}`;
      const recordRef = ref(db, recordPath);
      const updateData = {
        theme: themeId,
        themeName: themeName,
        sentiment: selectedSentiment || "neutral"
      };
      
      // Add sub-theme fields if selected
      if (subThemeId && subThemeName) {
        updateData.subTheme = subThemeId;
        updateData.subThemeName = subThemeName;
      } else {
        // Clear sub-theme if none selected
        updateData.subTheme = null;
        updateData.subThemeName = null;
      }

      // Add persona assignment
      if (selectedPersonaId) {
        updateData.personaId = selectedPersonaId;
        
        // Update persona's linkedFeedback
        const personaRef = ref(db, `userData/${orgId}/personas/${selectedPersonaId}`);
        const personaSnapshot = await get(personaRef);
        const personaData = personaSnapshot.val();
        const existingFeedback = personaData?.linkedFeedback || [];
        
        // Check if this feedback is already linked
        const feedbackLink = {
          feedbackId: record.id,
          uploadId: uploadId
        };
        
        if (!existingFeedback.find(f => f.feedbackId === record.id && f.uploadId === uploadId)) {
          const updatedFeedback = [...existingFeedback, feedbackLink];
          await update(personaRef, { linkedFeedback: updatedFeedback });
        }
      } else {
        // Remove persona assignment
        updateData.personaId = null;
        
        // Remove from persona's linkedFeedback if it was previously assigned
        if (record.personaId) {
          const oldPersonaRef = ref(db, `userData/${orgId}/personas/${record.personaId}`);
          const oldPersonaSnapshot = await get(oldPersonaRef);
          const oldPersonaData = oldPersonaSnapshot.val();
          if (oldPersonaData?.linkedFeedback) {
            const updatedFeedback = oldPersonaData.linkedFeedback.filter(
              f => !(f.feedbackId === record.id && f.uploadId === uploadId)
            );
            await update(oldPersonaRef, { linkedFeedback: updatedFeedback });
          }
        }
      }

      await update(recordRef, updateData);

      // Update theme record count if needed
      const themeRef = ref(db, `userData/${orgId}/customers/themes/${themeId}`);
      await update(themeRef, {
        name: themeName,
        lastUpdated: { ".sv": "timestamp" }
      });

      if (onThemeUpdated) {
        onThemeUpdated();
      }

      onClose();
    } catch (err) {
      console.error("Error updating theme:", err);
      setError("Error updating theme: " + (err.message || "Unknown error occurred"));
    } finally {
      setUpdating(false);
    }
  }

  return (
    <Dialog show={show} onClose={onClose} title="Edit Theme">
      <div className={style.editDialog}>
        <div className={style.feedbackPreview}>
          <label>Feedback:</label>
          <div className={style.feedbackText}>{record?.feedbackText || ""}</div>
          {record?.sentiment && (
            <div className={style.sentimentInfo}>
              <span className={`${style.sentimentBadge} ${style[record.sentiment]}`}>
                <Icon>
                  {record.sentiment === 'positive' ? 'sentiment_satisfied' : 
                   record.sentiment === 'negative' ? 'sentiment_dissatisfied' : 
                   'sentiment_neutral'}
                </Icon> Sentiment: {record.sentiment}
              </span>
            </div>
          )}
        </div>

        <div className={style.themeSelector}>
          <Select
            label="Theme"
            type="dropdown"
            options={[...themeOptions, { value: "Create new theme..." }]}
            selectedOptions={[creatingNew ? "Create new theme..." : selectedTheme]}
            onChange={handleThemeChange}
            multiselect={false}
          />
        </div>

        {selectedTheme && subThemes.length > 0 && (
          <div className={style.subThemeSelector}>
            <Select
              label="Sub-theme (Optional)"
              type="dropdown"
              options={[{ value: "None" }, ...subThemes.map(st => ({ value: st.name }))]}
              selectedOptions={[selectedSubTheme || "None"]}
              onChange={(e, index) => {
                if (index === 0) {
                  setSelectedSubTheme("");
                } else {
                  setSelectedSubTheme(subThemes[index - 1].name);
                }
              }}
              multiselect={false}
            />
          </div>
        )}

        <div className={style.sentimentSelector}>
          <Select
            label="Sentiment"
            type="dropdown"
            options={sentimentOptions}
            selectedOptions={[getSentimentDisplayValue(selectedSentiment)]}
            onChange={(e, index) => {
              setSelectedSentiment(sentimentOptions[index].sentimentValue);
            }}
            multiselect={false}
          />
        </div>

        <div className={style.personaSelector}>
          <PersonaLinkSelector
            selectedPersonaIds={selectedPersonaId ? [selectedPersonaId] : []}
            onChange={(personaId) => setSelectedPersonaId(personaId)}
            multiselect={false}
            orgId={orgId}
          />
        </div>

        {creatingNew && (
          <div className={style.newTheme}>
            <Input
              label="New Theme Name"
              value={newThemeName}
              onChange={(e) => setNewThemeName(e.target.value)}
              placeholder="e.g., Bug Reports"
            />
          </div>
        )}

        {error && (
          <div className={style.errorMessage}>{error}</div>
        )}

        <div className={style.actions}>
          <Button onClick={onClose} variants={["text"]} disabled={updating}>Cancel</Button>
          <Button onClick={handleSave} disabled={updating}>
            {updating ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default EditThemeDialog;

